// Single shared source of truth for the supported language set. Consumed by the
// detection helper, the language options/labels helper, and the dayjs locale loader so
// the list never drifts. Display labels are generated as endonyms via Intl.DisplayNames
// (see displayNames.ts), never hand-maintained. `dayjs` is the locale-bundle key used
// when lazily loading dayjs's own locale file (e.g. dayjs/locale/pt-br.js). `dir` is the
// document writing direction; it drives both <html dir> (I18nContext) and AntD's
// ConfigProvider `direction` (ThemeContext) so RTL languages flip the whole UI.
export const SUPPORTED_LANGUAGES = [
  { code: 'en', dayjs: 'en', dir: 'ltr' },
  { code: 'es', dayjs: 'es', dir: 'ltr' },
  { code: 'fr', dayjs: 'fr', dir: 'ltr' },
  { code: 'de', dayjs: 'de', dir: 'ltr' },
  { code: 'ja', dayjs: 'ja', dir: 'ltr' },
  { code: 'pt', dayjs: 'pt', dir: 'ltr' },
  { code: 'pt-BR', dayjs: 'pt-br', dir: 'ltr' },
  { code: 'ar', dayjs: 'ar', dir: 'rtl' },
  { code: 'cs', dayjs: 'cs', dir: 'ltr' },
  { code: 'da', dayjs: 'da', dir: 'ltr' },
  { code: 'it', dayjs: 'it', dir: 'ltr' },
  { code: 'ko', dayjs: 'ko', dir: 'ltr' },
  { code: 'nl', dayjs: 'nl', dir: 'ltr' },
  { code: 'pl', dayjs: 'pl', dir: 'ltr' },
  { code: 'sv', dayjs: 'sv', dir: 'ltr' },
] as const;

// Writing direction union ('ltr' | 'rtl') derived from the constant above.
export type TextDirection = (typeof SUPPORTED_LANGUAGES)[number]['dir'];

// Narrow union of every supported language code, derived from the constant above
// (e.g. 'en' | 'es' | … | 'pt' | 'pt-BR' | 'cs' | …). `pt` is generic/European Portuguese
// (the default Portuguese) while `pt-BR` is the Brazil-specific variant.
export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

// localStorage key for the persisted language preference. Kept in one place so the write
// (I18nProvider) and the initial read (each app's i18n bootstrap) can never drift apart.
export const LANGUAGE_STORAGE_KEY = 'language';
